/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import {
  Clock,
  Users,
  Award,
  CheckCircle,
  ArrowLeft,
  Calendar,
  Phone,
  Mail,
  Target,
  Shield,
  Anchor,
  IndianRupee,
} from "lucide-react";
import { coursesData } from "../data/courses";

const benefits = [
  {
    title: "Certified Instructors",
    description:
      "Industry experts with years of sea experience and professional certifications",
    icon: Award,
  },
  {
    title: "Small Class Sizes",
    description:
      "Personalized attention with maximum 12 students per batch for better learning",
    icon: Users,
  },
  {
    title: "100% Pass Rate",
    description:
      "Proven track record of student success with comprehensive support",
    icon: CheckCircle,
  },
  {
    title: "Flexible Scheduling",
    description: "Multiple batches throughout the year to fit your schedule",
    icon: Calendar,
  },
  {
    title: "Job Placement",
    description: "Career guidance and job placement assistance for graduates",
    icon: Target,
  },
  {
    title: "Modern Equipment",
    description:
      "State-of-the-art training equipment and simulation technology",
    icon: Shield,
  },
];

const CourseDetail = () => {
  const { category } = useParams();

  useEffect(() => {
    if (category === "competency-training") {
      window.location.href = "/courses/competency-training";
      return;
    }
    if (category === "cadetship-training") {
      window.location.href = "/courses/cadetship-training";
      return;
    }
  }, [category]);

  const categoryData = coursesData[category as keyof typeof coursesData];

  // Scroll to top when component mounts or category changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [category]);

  if (!categoryData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 pt-20">
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-2xl">
          <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <Anchor className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="mb-4 text-2xl font-bold text-slate-900">
            Category Not Found
          </h1>
          <p className="mb-6 text-slate-600">
            The requested training category could not be found.
          </p>
          <Link
            to="/courses"
            className="inline-flex items-center rounded-xl bg-teal-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-teal-700"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Courses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-teal-50/20">
      {/* Header */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('/placeholder.svg?height=600&width=1200')] bg-cover bg-center"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent"></div>

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/courses"
              className="mb-6 inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-blue-200 backdrop-blur-sm transition-all duration-300 hover:bg-white/20 hover:text-white"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Categories
            </Link>
            <br />

            <h1 className="mb-4 text-4xl font-bold text-white sm:text-5xl lg:text-6xl">
              {categoryData.title}
            </h1>
            <p className="max-w-3xl text-xl leading-relaxed text-slate-300">
              {categoryData.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl">
              Choose Your Course
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-slate-600">
              Select from our comprehensive selection of{" "}
              {categoryData.title.toLowerCase()} courses designed for
              professional excellence
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {categoryData.courses.map((course: any, index: any) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex" // Add flex to make all cards equal height
              >
                <div className="group flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
                  {/* Course Header */}
                  <div className="flex-shrink-0 p-6 pb-4">
                    <h3 className="mb-2 text-lg font-bold text-slate-900 transition-colors duration-300 group-hover:text-teal-600">
                      {course.name}
                    </h3>
                    <h4 className="mb-3 text-sm font-medium leading-tight text-slate-600">
                      {course.fullName}
                    </h4>
                  </div>

                  {/* Course Content - Flexible height */}
                  <div className="flex-grow px-6">
                    <p className="mb-6 text-sm leading-relaxed text-slate-600">
                      {course.description}
                    </p>
                  </div>

                  {/* Course Details & Button - Fixed at bottom */}
                  <div className="mt-auto flex-shrink-0 px-6 pb-6">
                    <div className="mb-6 space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center text-slate-500">
                          <Clock size={16} className="mr-2" />
                          Duration
                        </div>
                        <span className="font-medium text-slate-900">
                          {course.duration}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center text-slate-500">
                          <IndianRupee size={16} className="mr-2" />
                          Investment
                        </div>
                        <span className="text-lg font-bold text-teal-600">
                          {course.price}
                        </span>
                      </div>
                    </div>

                    {/* Enroll Button */}
                    <Link
                      to={`/payment?training=${encodeURIComponent(category || "")}&course=${encodeURIComponent(course.name)}&price=${encodeURIComponent(course.price)}`}
                      className="block w-full rounded-xl bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-3 text-center text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:from-teal-700 hover:to-teal-800 hover:shadow-xl"
                    >
                      Enroll Now
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="bg-slate-900 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold text-white sm:text-4xl">
              Why Choose Our Training?
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-slate-300">
              Industry-leading training programs designed for maritime
              excellence and career advancement
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group text-center"
              >
                <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-lg transition-transform duration-300 group-hover:scale-110">
                  <benefit.icon className="h-8 w-8" />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-white">
                  {benefit.title}
                </h3>
                <p className="leading-relaxed text-slate-300">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-gradient-to-br from-teal-600 via-blue-600 to-cyan-600 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-4 text-3xl font-bold text-white sm:text-4xl">
                Ready to Begin Your Training?
              </h2>
              <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-blue-100">
                Contact our admissions team to learn more about enrollment,
                schedules, and financing options. Start your maritime career
                journey today.
              </p>
              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <a
                  href="tel:+918427775865"
                  className="inline-flex items-center justify-center rounded-xl bg-white px-8 py-4 text-lg font-semibold text-teal-600 shadow-lg transition-all duration-300 hover:scale-105 hover:bg-slate-100 hover:shadow-xl"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call Admissions
                </a>
                <a
                  href="mailto:info@nautilustimes.com"
                  className="inline-flex items-center justify-center rounded-xl border-2 border-white/20 bg-white/10 px-8 py-4 text-lg font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:bg-white/20"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Email Inquiry
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseDetail;
